import { useEffect, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge, badgeStatus } from "@/components/Shared";
import JobLink from "@/components/JobLink";

/**
 * Jobs already entered on the PO being typed, listed like the Jobs search, so an order
 * isn't keyed in twice. A PO often covers several products: only a row with the same
 * product code is called out as a likely duplicate. Never blocks saving.
 */
export default function PoMatches({ po, productCode, excludeId }) {
  const [found, setFound] = useState(null);

  useEffect(() => {
    const value = (po || "").trim();
    if (value.length < 3) {
      setFound(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get("/jobs/lookup/po", { params: { po_number: value, product_code: productCode || undefined, exclude_id: excludeId || undefined } })
        .then(({ data }) => !cancelled && setFound(data))
        .catch(() => !cancelled && setFound(null));
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [po, productCode, excludeId]);

  if (!found || !found.jobs.length) return null;
  const duplicate = found.jobs.some((j) => j.same_product);

  return (
    <div className={`col-span-full border rounded-sm ${duplicate ? "border-rose-300 bg-rose-50/60" : "border-amber-300 bg-amber-50/60"}`}
      data-testid="po-matches">
      <div className={`px-3 py-2 flex items-start gap-2 text-xs ${duplicate ? "text-rose-900" : "text-amber-900"}`}>
        {duplicate ? <AlertTriangle size={14} className="mt-0.5 shrink-0" /> : <Info size={14} className="mt-0.5 shrink-0" />}
        <span>
          PO <b className="font-mono-num">{found.po_number}</b> is already on{" "}
          <b>{found.total} job{found.total === 1 ? "" : "s"}</b> in the ERP.
          {duplicate
            ? " One has the same product code: check it isn't this order before saving."
            : " Different products on the same PO are normal; check yours isn't listed."}
        </span>
      </div>
      <div className="max-h-48 overflow-y-auto border-t border-black/5 bg-white">
        <table className="w-full text-xs">
          <tbody>
            {found.jobs.map((j) => (
              <tr key={j.id} className={`border-t border-neutral-100 first:border-0 ${j.same_product ? "bg-rose-50" : ""}`}>
                <td className="px-3 py-1.5 font-mono-num whitespace-nowrap">
                  <JobLink id={j.id} number={j.mpwo_number || j.job_number} newTab />
                </td>
                <td className="px-3 py-1.5"><div className="truncate max-w-[9rem]" title={j.customer_name}>{j.customer_name}</div></td>
                <td className="px-3 py-1.5"><div className="truncate max-w-[14rem]" title={j.product_name}>{j.product_name}</div></td>
                <td className="px-3 py-1.5 font-mono-num">
                  <div className="truncate max-w-[9rem]" title={j.product_code}>{j.product_code || "—"}</div>
                  {j.same_product && <div className="text-[10px] uppercase tracking-widest text-rose-700 font-sans">same product</div>}
                </td>
                <td className="px-3 py-1.5 text-right font-mono-num whitespace-nowrap">{j.quantity?.toLocaleString("en-IN")}</td>
                <td className="px-3 py-1.5 whitespace-nowrap"><StatusBadge status={badgeStatus(j)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {found.total > found.jobs.length && (
        <div className="px-3 py-1.5 text-[11px] text-neutral-500 border-t border-black/5">Newest {found.jobs.length} of {found.total} shown.</div>
      )}
    </div>
  );
}
